import { Button, DatePicker, Label, Select, Text } from '@mercai/clever'
import { useTranslation } from 'react-i18next'

import { OrderPaymentStatusEnum, SaleOrderProps } from '@/types'

import type { OrderPaymentPaidAtFormProps } from './order-payment-paid-at-form.type'

import styles from './order-payment-paid-at-form.module.css'
import { useValidationErrors } from '@/helpers'
import { schemaOrderPaymentsPaidAt } from './order-payment-paid-at-form.constants'
import { useEffect, useState } from 'react'

export const OrderPaymentPaidAtForm = ({
  onChangeValues,
  values,
  onBack,
  onHandleSubmit,
}: OrderPaymentPaidAtFormProps) => {
  const { t: tEnums } = useTranslation('enums')

  const { t } = useTranslation('componentsOrderPaymentPaidAtForm')

  const { isValid, errors, onHandleListenErrors } = useValidationErrors(
    schemaOrderPaymentsPaidAt(t),
    values,
  )

  const [paidAtLast, setPaidAtLast] = useState<string[]>([])

  const onHandleSubmitLocal = () => {
    if (!isValid) {
      onHandleListenErrors(true)
      return
    }

    onHandleListenErrors(false)
    onHandleSubmit()
  }

  const onHandleValuesArray = (
    objectIndex: number,
    attributeKey: keyof SaleOrderProps[keyof SaleOrderProps],
    value?: string | number | boolean,
  ) => {
    const originalArray = Array.isArray(values?.payments)
      ? values?.payments
      : []

    const updatedArray = [...(originalArray as any[])] // eslint-disable-line

    updatedArray[objectIndex] = {
      ...updatedArray[objectIndex],
      [attributeKey]: value,
    }

    onChangeValues({ ...values, payments: updatedArray })
  }

  useEffect(() => {
    values?.payments?.forEach((payment) => {
      setPaidAtLast((prev) => [...prev, payment?.paidAt])
    })
  }, [values])

  return (
    <>
      <div className={styles.core}>
        {values?.payments?.map((payment, index) => {
          const showParcelLabel = ['partial'].includes(payment?.form)

          const parcelLabel = `(${payment?.instalment}/${values?.payments?.length})`

          return (
            <div key={payment?.uuid} className={styles.content}>
              <div>
                <Text variant="text-large-bold" className={styles.description}>
                  {t('subtitle')} (
                  {tEnums(`finance_payment_form_${payment?.form}`)}
                  {showParcelLabel && ` ${parcelLabel}`})
                </Text>
              </div>
              <div>
                <Label>
                  {t('statusLabel')}
                  <span className={styles.required}>*</span>
                </Label>
                <Select
                  options={Object.keys(OrderPaymentStatusEnum).map(
                    (status) => ({
                      label: tEnums(`order_payment_status_${status}`),
                      value: status,
                    }),
                  )}
                  value={payment?.status}
                  onChange={(newValue) =>
                    onHandleValuesArray(
                      index,
                      'status' as keyof SaleOrderProps[keyof SaleOrderProps],
                      newValue,
                    )
                  }
                  error={
                    (errors?.[`payments[${index}]`] as Record<string, string>)
                      ?.status
                  }
                />
              </div>

              <div>
                <Label>{t('paidAtLastLabel')}</Label>
                <DatePicker
                  placeholder={'dd/mm/yyyy'}
                  value={paidAtLast?.[index] || undefined}
                  disabled
                />
              </div>

              <div>
                <Label>
                  {t('paidAtNewLabel')}
                  <span className={styles.required}>*</span>
                </Label>
                <DatePicker
                  placeholder={'dd/mm/yyyy'}
                  value={payment?.paidAt}
                  onChange={(newValue) =>
                    onHandleValuesArray(
                      index,
                      'paidAt' as keyof SaleOrderProps[keyof SaleOrderProps],
                      newValue,
                    )
                  }
                  error={
                    (errors?.[`payments[${index}]`] as Record<string, string>)
                      ?.paidAt
                  }
                />
              </div>
            </div>
          )
        })}
      </div>

      <div className={styles.footer}>
        <Button variant="secondary" onClick={onBack}>
          {t('buttonCancel')}
        </Button>

        <Button onClick={onHandleSubmitLocal}>{t('buttonSubmit')}</Button>
      </div>
    </>
  )
}
